import apiClient from "./apiClient";

export type TaxJurisdictionType =
  | "STATE"
  | "PARISH"
  | "CITY"
  | "SPECIAL_DISTRICT"
  | "OTHER";

export type FilingStatus =
  | "NOT_FILED"
  | "READY_TO_FILE"
  | "FILED"
  | "AMENDED";

export interface TaxJurisdictionSetting {
  _id?: string;
  code: string;
  name: string;
  type: TaxJurisdictionType;
  rateBasisPoints: number;
  state?: string;
  parish?: string;
  city?: string;
  specialDistrict?: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isDefault: boolean;
  active: boolean;
}

export interface JurisdictionTaxResult {
  id: string;
  code: string;
  name: string;
  type: TaxJurisdictionType;
  rateBasisPoints: number;
  taxableSalesCents: number;
  taxCollectedCents: number;
  calculatedTaxCents: number;
}

export interface TransactionTaxAudit {
  id: string;
  eventId: string;
  eventName: string;
  occurredAt: string;
  type: "Sale" | "Refund";
  grossSalesCents: number;
  discountsCents: number;
  refundsCents: number;
  netSalesCents: number;
  taxableSalesCents: number;
  nonTaxableSalesCents: number;
  taxCollectedCents: number;
  taxLiabilityCents: number;
  differenceCents: number;
  paymentMethods: string[];
  paymentSources: string[];
  lines: Array<{
    id: string;
    itemName: string;
    quantity: number;
    grossCents: number;
    discountCents: number;
    netCents: number;
    taxable: boolean;
  }>;
}

export interface EventTaxResult {
  eventId: string;
  eventName: string;
  transactionCount: number;
  grossSalesCents: number;
  discountsCents: number;
  refundsCents: number;
  netSalesCents: number;
  taxableSalesCents: number;
  nonTaxableSalesCents: number;
  taxCollectedCents: number;
  stateTaxDueCents: number;
  cityTaxDueCents: number;
  otherTaxDueCents: number;
  totalTaxDueCents: number;
}

export interface MonthlyTaxCalculation {
  version: number;
  month: string;
  timezone: string;
  periodStart: string;
  periodEndExclusive: string;
  calculatedAt: string;
  grossSalesCents: number;
  discountsCents: number;
  refundsCents: number;
  netSalesCents: number;
  taxableSalesCents: number;
  nonTaxableSalesCents: number;
  taxCollectedCents: number;
  taxLiabilityCents: number;
  differenceCents: number;
  transactionCount: number;
  eventCount: number;
  includedPaymentMethods: string[] | null;
  availablePaymentMethods: string[];
  includedPaymentSources: string[] | null;
  availablePaymentSources: string[];
  jurisdictions: JurisdictionTaxResult[];
  events: EventTaxResult[];
  paymentMethods: Array<{
    paymentMethod: string;
    transactionCount: number;
    totalCollectedCents: number;
  }>;
  transactions: TransactionTaxAudit[];
  fingerprint: string;
}

export type SquareMatchStatus =
  | "MATCHED"
  | "SQUARE_ONLY"
  | "VENDORORBIS_ONLY"
  | "AMOUNT_MISMATCH"
  | "REFUND_MISMATCH"
  | "MISSING_SQUARE_ID"
  | "MANUAL_SQUARE_ENTRY"
  /** Square payment that could belong to more than one event. */
  | "NEEDS_EVENT";

export interface SquareReportSnapshot {
  version: 1;
  source: "SQUARE_DIRECT";
  periodStart: string;
  periodEndExclusive: string;
  reportIntervals?: Array<{ start: string; endExclusive: string }>;
  timezone: string;
  syncedAt: string;
  locations: Array<{
    id: string;
    name: string;
    timezone?: string;
    status: string;
  }>;
  selectedLocationIds: string[];
  currency: string;
  paymentCount: number;
  paymentTotalCents: number;
  salesCents: number | null;
  taxCents: number;
  tipsCents: number;
  discountsCents: number;
  refundsCents: number;
  netProcessedCents: number;
  processingFeesCents: number;
  estimatedNetDepositCents: number;
  paymentsWithoutOrderData: number;
  fingerprint: string;
}

export interface SquareReconciliationResult {
  version: 1;
  calculatedAt: string;
  vendorCardSalesCents: number;
  squareCardSalesCents: number;
  cardSalesDifferenceCents: number;
  vendorTaxCollectedCents: number;
  squareTaxCollectedCents: number;
  taxDifferenceCents: number;
  vendorRefundsCents: number;
  squareRefundsCents: number;
  refundsDifferenceCents: number;
  vendorTransactionCount: number;
  squareTransactionCount: number;
  transactionCountDifference: number;
  discrepancy: boolean;
  statusCounts: Record<SquareMatchStatus, number>;
  audit: Array<{
    key: string;
    status: SquareMatchStatus;
    vendorTransactionId?: string;
    squarePaymentId?: string;
    squareRefundId?: string;
    squareOrderId?: string;
    occurredAt: string;
    vendorAmountCents: number;
    squareAmountCents: number;
    differenceCents: number;
    provenance: "SQUARE_DIRECT" | "SQUARE_LEGACY" | "MANUAL_SQUARE" | "SQUARE_API";
    /** NEEDS_EVENT: the events it could belong to. */
    candidateEventIds?: string[];
    /** Event a Square-only payment was matched to. */
    eventId?: string;
  }>;
  fingerprint: string;
}

export interface SquareReportEnvelope {
  connected: boolean;
  status: "LIVE" | "CACHED" | "ERROR" | "NOT_SYNCED";
  snapshot: SquareReportSnapshot | null;
  reconciliation: SquareReconciliationResult | null;
  error: null | { code: string; message: string; retryable: boolean };
  filedSnapshot: SquareReportSnapshot | null;
  filedReconciliation: SquareReconciliationResult | null;
  differsFromFiled: boolean;
}

export interface MonthlyTaxReportResponse {
  current: MonthlyTaxCalculation;
  filing: {
    status: FilingStatus;
    filedAt?: string | null;
    amountPaidCents?: number | null;
    confirmationNumber?: string;
    notes?: string;
    filedSnapshot?: MonthlyTaxCalculation | null;
    filedSquareSnapshot?: SquareReportSnapshot | null;
    filedSquareReconciliation?: SquareReconciliationResult | null;
    filingHistory: Array<Record<string, unknown>>;
  };
  differsFromFiled: boolean;
  liabilityDifferenceFromFiledCents: number;
  square: SquareReportEnvelope;
}

const normalizeMonthlyTaxReport = (
  report: MonthlyTaxReportResponse,
): MonthlyTaxReportResponse => ({
  ...report,
  current: {
    ...report.current,
    includedPaymentMethods:
      report.current?.includedPaymentMethods ?? null,
    availablePaymentMethods:
      report.current?.availablePaymentMethods ?? [],
    includedPaymentSources:
      report.current?.includedPaymentSources ?? null,
    availablePaymentSources:
      report.current?.availablePaymentSources ?? [],
    jurisdictions: report.current?.jurisdictions ?? [],
    events: report.current?.events ?? [],
    paymentMethods: report.current?.paymentMethods ?? [],
    transactions: report.current?.transactions ?? [],
  },
  filing: {
    ...report.filing,
    filingHistory: report.filing?.filingHistory ?? [],
  },
  square: report.square ?? {
    connected: false,
    status: "NOT_SYNCED",
    snapshot: null,
    reconciliation: null,
    error: null,
    filedSnapshot: null,
    filedReconciliation: null,
    differsFromFiled: false,
  },
});

export const salesTaxApi = {
  settings: async () => {
    const { data } = await apiClient.get<{ jurisdictions: TaxJurisdictionSetting[] }>(
      "/finance/tax/jurisdictions",
    );
    return data.jurisdictions;
  },
  saveSettings: async (jurisdictions: TaxJurisdictionSetting[]) => {
    const { data } = await apiClient.put<{ jurisdictions: TaxJurisdictionSetting[] }>(
      "/finance/tax/jurisdictions",
      { jurisdictions },
    );
    return data.jurisdictions;
  },
  calculate: async (
    month: string,
    timezone = "America/Chicago",
    includedPaymentMethods?: string[] | null,
    includedPaymentSources?: string[] | null,
    startDate?: string,
    endDate?: string,
    squareLocationIds?: string[],
    eventIds?: string[],
  ) => {
    const { data } = await apiClient.post<MonthlyTaxReportResponse>(
      "/finance/tax/calculate",
      {
        month,
        timezone,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(squareLocationIds?.length ? { squareLocationIds } : {}),
        ...(eventIds?.length ? { eventIds } : {}),
        ...(includedPaymentMethods !== null && includedPaymentMethods !== undefined
          ? { includedPaymentMethods }
          : {}),
        ...(includedPaymentSources !== null && includedPaymentSources !== undefined
          ? { includedPaymentSources }
          : {}),
      },
    );
    return normalizeMonthlyTaxReport(data);
  },
  refreshSquare: async (
    month: string,
    timezone = "America/Chicago",
    startDate?: string,
    endDate?: string,
    squareLocationIds?: string[],
    eventIds?: string[],
  ) => {
    const { data } = await apiClient.post<{ square: SquareReportEnvelope }>(
      `/finance/tax/reports/${month}/square/refresh`,
      {
        timezone,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(squareLocationIds?.length ? { squareLocationIds } : {}),
        ...(eventIds?.length ? { eventIds } : {}),
      },
    );
    return data.square;
  },
  /** Say which event a Square payment (or refund) belongs to; null clears it. */
  assignSquarePayment: async (
    squareId: string,
    kind: "payment" | "refund",
    eventId: string | null,
  ) => {
    const { data } = await apiClient.put<{
      squareId: string;
      kind: "payment" | "refund";
      eventId: string | null;
    }>("/finance/tax/square-assignments", { squareId, kind, eventId });
    return data;
  },
  updateFiling: async (
    month: string,
    body: {
      status: FilingStatus;
      timezone?: string;
      filedAt?: string;
      amountPaidCents?: number | null;
      confirmationNumber?: string;
      notes?: string;
      includedPaymentMethods?: string[] | null;
      includedPaymentSources?: string[] | null;
    },
  ) => {
    const { data } = await apiClient.put<MonthlyTaxReportResponse>(
      `/finance/tax/reports/${month}/filing`,
      body,
    );
    return normalizeMonthlyTaxReport(data);
  },
  exportCsv: async (
    month: string,
    timezone = "America/Chicago",
    includedPaymentMethods?: string[] | null,
    includedPaymentSources?: string[] | null,
    startDate?: string,
    endDate?: string,
    eventIds?: string[],
  ) => {
    const { data } = await apiClient.get(
      `/finance/tax/reports/${month}/export.csv`,
      {
        params: {
          timezone,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          ...(eventIds?.length ? { eventIds: eventIds.join(",") } : {}),
          ...(includedPaymentMethods !== null &&
          includedPaymentMethods !== undefined
            ? { paymentMethods: includedPaymentMethods.join(",") }
            : {}),
          ...(includedPaymentSources !== null &&
          includedPaymentSources !== undefined
            ? { paymentSources: includedPaymentSources.join(",") }
            : {}),
        },
        responseType: "blob",
      },
    );
    const url = URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sales-tax-${month}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  },
};
